import { CollectionPreferencesProps, Icon, Link } from '@amzn/awsui-components-core';
import {
  ContentRequest,
  ContentRequestState,
  ContentRequestType,
} from '@amzn/awscat-pattern-library-service-model';
import { DateTime } from 'luxon';
import React from 'react';
import { getContentPath } from '../../common/ContentPaths';
import { dateComparator, stringComparator } from '../utils/Comparator';
import ContentRequestActionColumn from './ContentRequestActionColumn';
import ContentRequestStateBadge from './ContentRequestStateBadge';
import DeleteContentSuggestionButton from './DeleteContentSuggestionButton';
import ContentRequestLink from './ContentRequestLink';

export const ContentRequestIdCol = {
  cell: (i: ContentRequest) => i.requestId,
  header: 'Request id',
  id: 'requestId',
};

export const ContentRequestIdSort = {
  comparator: stringComparator((i) => i.requestId),
  id: 'requestId',
};

export const RequestSelOpt = {
  editable: true,
  id: 'requestId',
  label: 'Request id',
  visible: false,
};

export const OwnerCol = {
  allowLineWrap: true,
  cell: (i: ContentRequest) => (i.userName ? i.userName : i.userId),
  header: 'Requestor',
  id: 'userName',
  width: 150,
};

export const OwnerSort = {
  comparator: stringComparator((i) => i.userId),
  field: 'Requestor',
  id: 'userName',
};

export const OwnerSelOpt = {
  editable: true,
  id: 'userName',
  label: 'Requestor',
  visible: true,
};

export const ContentNameCol = {
  cell: (i: ContentRequest) => <ContentRequestLink contentRequest={i} />,
  header: 'Content title',
  id: 'contentName',
  width: 300,
};

export const ContentNameSort = {
  comparator: stringComparator((i) => i.contentName),
  id: 'contentName',
};

export const ContentNameSelOpt = {
  editable: false,
  id: 'contentName',
  label: 'Content title',
  visible: true,
};

export const getContentRequestLink = (contentRequest: ContentRequest) => {
  const { contentId, requestId, requestType } = contentRequest;
  return requestType === ContentRequestType.Update
    ? getContentPath({ contentId: contentId ?? '', mode: 'publication' })
    : `/review-content-request/${requestId}`;
};

export const ContentRequestNameCol = {
  cell: (i: ContentRequest) => <ContentRequestLink contentRequest={i} />,
  header: 'Name',
  id: 'contentRequestName',
  width: 200,
};

export const ContentRequestNameSort = {
  comparator: stringComparator((i) => i.contentName),
  id: 'contentRequestName',
};

export const ContentRequestNameSelOpt = {
  editable: false,
  id: 'contentRequestName',
  label: 'Name',
  visible: true,
};

export const ContentTypeCol = {
  cell: (i: ContentRequest) => i.contentType,
  header: 'Content type',
  id: 'contentType',
  width: 150,
};

export const ContentTypeSort = {
  comparator: stringComparator((i) => i.contentType),
  id: 'contentType',
};

export const ContentTypeSelOpt = {
  editable: true,
  id: 'contentType',
  label: 'Content type',
  visible: true,
};

export const StateCol = {
  cell: (i: ContentRequest) => <ContentRequestStateBadge state={i.state} />,
  header: 'State',
  id: 'state',
};

export const StateSort = {
  comparator: stringComparator((i) => i.state),
  id: 'state',
};

export const StateSelOpt = {
  editable: true,
  id: 'state',
  label: 'State',
  visible: true,
};

export const CreatedDateCol = {
  cell: (i: ContentRequest) => i.createdDate.toLocaleString(DateTime.DATETIME_FULL),
  header: 'Requested at',
  id: 'createdDate',
};

export const CreatedDateSort = {
  comparator: dateComparator((i) => i.createdDate),
  id: 'createdDate',
};

export const CreatedDateSelOpt = {
  editable: true,
  id: 'createdDate',
  label: 'Requested at',
  visible: true,
};

export const UpdatedDateCol = {
  cell: (i: ContentRequest) =>
    i.lastUpdatedAt ? i.lastUpdatedAt.toLocaleString(DateTime.DATETIME_FULL) : '--',
  header: 'Updated at',
  id: 'lastUpdatedAt',
};

export const UpdatedDateSort = {
  comparator: dateComparator((i) => i.lastUpdatedAt),
  id: 'lastUpdatedAt',
};

export const UpdatedDateSelOpt = {
  editable: true,
  id: 'lastUpdatedAt',
  label: 'Updated at',
  visible: true,
};

export const AssignedDateCol = {
  cell: (item: ContentRequest) =>
    item.assignedDate ? item.assignedDate.toLocaleString(DateTime.DATETIME_FULL) : '--',
  header: 'Assigned on',
  id: 'assignedDate',
  width: 150,
};

export const AssignedDateSort = {
  comparator: dateComparator((i) => i.assignedDate),
  id: 'assignedDate',
};

export const AssignedDateSelOpt = {
  editable: true,
  id: 'assignedDate',
  label: 'Assigned on',
  visible: true,
};

export const AgeOfAssignmentCol = {
  cell: (item: ContentRequest) => {
    const dateDiff = item.assignedDate?.diffNow('hours');
    const dateDiffToDisplay = DateTime.local()
      .plus({ hours: dateDiff?.hours })
      .toRelative()
      ?.replace(' ago', '');
    return <div>{dateDiffToDisplay}</div>;
  },
  header: 'Age of request',
  id: 'ageOfAssignmentCol',
  width: 150,
};

export const AgeOfAssignmentSort = {
  comparator: dateComparator((i) => i.assignedDate),
  id: 'ageOfAssignmentCol',
};

export const AgeOfAssignmentSelOpt = {
  editable: true,
  id: 'ageOfAssignmentCol',
  label: 'Age of request',
  visible: true,
};

export const DuplicatedCol = {
  // @ts-expect-error TS7030 - strict migration
  cell: (item: ContentRequest) => {
    if (item.state === ContentRequestState.DUPLICATED) {
      return <Icon name="status-positive" />;
    }
  },
  header: 'Duplicate',
  id: 'duplicated',
};

export const DuplicatedSort = {
  comparator: stringComparator((i) => i.referenceDuplicated),
  id: 'duplicated',
};

export const DuplicatedSelOpt = {
  editable: true,
  id: 'duplicated',
  label: 'Duplicate',
  visible: true,
};

export const DuplicatedOfCol = {
  cell: (i: ContentRequest) =>
    i.referenceDuplicated ? (
      <Link variant="secondary" href={`/review-content-request/${i.referenceDuplicated}`}>
        View duplicated suggestion
      </Link>
    ) : null,
  header: 'Duplicate of',
  id: 'duplicatedOf',
};

export const DuplicatedOfSort = {
  comparator: stringComparator((i) => i.referenceDuplicated),
  id: 'duplicatedOf',
};

export const DuplicatedOfColSelOpt = {
  editable: true,
  id: 'duplicatedOf',
  label: 'Duplicate of',
  visible: true,
};

export const ContentSuggestionDeleteCol = {
  cell: (i: ContentRequest) => {
    return (
      <div className="awsui-cat-flex awsui-cat-flex-end">
        <DeleteContentSuggestionButton contentSuggestion={i} />
      </div>
    );
  },
  header: '',
  id: 'deleteSuggestion',
};

export const ContentSuggestionDeleteColSelOpt = {
  editable: false,
  id: 'deleteSuggestion',
  label: 'Delete suggestion',
  visible: true,
};

export const ContentRequestDescriptionCol = {
  cell: (item: ContentRequest) => <ContentRequestActionColumn contentRequest={item} />,
  header: 'Description',
  id: 'description',
  width: 800,
};

export const ContentRequestDescriptionSort = {
  comparator: stringComparator((i) => i.description),
  id: 'description',
};

export const ContentRequestDescriptionSelOpt = {
  editable: false,
  id: 'description',
  label: 'Description',
  visible: true,
};

export const DefaultContentRequestSortableColumns: Array<{
  id: string;
  comparator: (a: ContentRequest, b: ContentRequest) => number;
}> = [
  ContentRequestIdSort,
  OwnerSort,
  ContentNameSort,
  ContentRequestNameSort,
  ContentTypeSort,
  AssignedDateSort,
  AgeOfAssignmentSort,
  StateSort,
  CreatedDateSort,
  DuplicatedSort,
  DuplicatedOfSort,
  UpdatedDateSort,
  ContentRequestDescriptionSort,
];

export const DefaultContentRequestSelectorOptions: CollectionPreferencesProps.VisibleContentOption[] =
  [
    RequestSelOpt,
    OwnerSelOpt,
    ContentNameSelOpt,
    ContentRequestNameSelOpt,
    ContentTypeSelOpt,
    AssignedDateSelOpt,
    AgeOfAssignmentSelOpt,
    StateSelOpt,
    CreatedDateSelOpt,
    DuplicatedSelOpt,
    DuplicatedOfColSelOpt,
    UpdatedDateSelOpt,
    ContentSuggestionDeleteColSelOpt,
    ContentRequestDescriptionSelOpt,
  ];

export const DefaultContentRequestTableColumns = [
  ContentNameCol,
  ContentTypeCol,
  StateCol,
  CreatedDateCol,
];

export const ContentRequestAssignmentTableColumns = [
  ContentRequestNameCol,
  ContentTypeCol,
  AssignedDateCol,
  AgeOfAssignmentCol,
  ContentRequestDescriptionCol,
];
